import { EntitySearchType } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';

import { isEntity } from '@helpers/utils/entity/entity';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useRecentList } from '@terminal/views/turbo-chart/hooks/use-recent-list/useRecentList';
import { useThemeSearch } from '@terminal/widgets/search-asset-widget/hooks/use-theme-search/useThemeSearch';
import { useMemo } from 'react';

import {
  MentionAssetCandidate,
  MentionEntityCandidate,
  MentionThemeCandidate,
} from './mention-source-types';

const entityMentionLabel = (entity: Entity) =>
  entity.ticker ? formatTicker(entity) : entity.name;

export interface UseMentionEntitySourceResult {
  setQuery: (query: string) => void;
  clear: () => void;
  loading: boolean;
  entities: MentionEntityCandidate[];
  themes: MentionThemeCandidate[];
  recent: MentionAssetCandidate[];
}

export const useMentionEntitySource = (
  enabled = true
): UseMentionEntitySourceResult => {
  const {
    tags,
    searchTags,
    clearTags,
    loading: entitiesLoading,
  } = useTagsSearch({ searchType: EntitySearchType.Entity });
  const { themeSearchResults, handleTextChange } = useThemeSearch(
    false,
    enabled
  );
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { recentlyOpenedThemesOrEntities } = useRecentList();

  const setQuery = (query: string) => {
    searchTags(query);
    handleTextChange(query);
  };

  const clear = () => {
    clearTags();
    handleTextChange('');
  };

  const entities = useMemo<MentionEntityCandidate[]>(
    () =>
      tags.map(entity => ({
        kind: 'entity',
        id: entity.tag,
        label: entityMentionLabel(entity),
        entity,
      })),
    [tags]
  );

  const themes = useMemo<MentionThemeCandidate[]>(
    () =>
      themeSearchResults.map(theme => ({
        kind: 'theme',
        id: theme.id,
        label: theme.name,
        theme,
      })),
    [themeSearchResults]
  );

  const recent = useMemo<MentionAssetCandidate[]>(
    () =>
      recentlyOpenedThemesOrEntities.reduce<MentionAssetCandidate[]>(
        (acc, item) => {
          if (isEntity(item)) {
            if (isBasicUser && item.gated) {
              return acc;
            }
            acc.push({
              kind: 'entity',
              id: item.tag,
              label: entityMentionLabel(item),
              entity: item,
            });
          } else if (item.version === 2) {
            acc.push({
              kind: 'theme',
              id: item.id,
              label: item.name,
              theme: item,
            });
          }
          return acc;
        },
        []
      ),
    [recentlyOpenedThemesOrEntities, isBasicUser]
  );

  return {
    setQuery,
    clear,
    loading: entitiesLoading,
    entities,
    themes,
    recent,
  };
};
